import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "store/rootReducer";
import styled from "styled-components/macro";

import { IGroupByMap } from "models";

import { XdaDataPickerSelector } from "./XdaDataPickerSelector";

const DEFAULT_TOP: IGroupByMap = {
  title: "Entity Name",
  property: "Header.EntityName" //need something here or else group by will throw error
};
const DEFAULT_LEFT: IGroupByMap = { title: "Zone", property: "Geology.Zone" };
const DEFAULT_RIGHT: IGroupByMap = {
  title: "Vintage (Year)",
  property: "Dates.Vintage_Year"
};

export interface XdaDataPickerModel {
  onChange: (fields: IGroupByMap[]) => void;
  /** Saved field selections to restore on mount — order: [bottom, left, top, right]. */
  initialFields?: IGroupByMap[];
}

///component for picking 4 data fields in clockwise direction starting with south
export default function XdaDataPicker(input: XdaDataPickerModel) {
  const groupBy = useSelector((state: RootState) => state.groupBy.globalGroupBy);

  // Prefer saved initialFields over hardcoded defaults so the picker restores correctly after save.
  const [bottomValue, setBottomValue] = useState<IGroupByMap>(
    input.initialFields?.[0] ?? groupBy
  );
  const [topValue, setTopValue] = useState<IGroupByMap>(
    input.initialFields?.[2] ?? DEFAULT_TOP
  );
  const [leftValue, setLeftValue] = useState<IGroupByMap>(
    input.initialFields?.[1] ?? DEFAULT_LEFT
  );
  const [rightValue, setRightValue] = useState<IGroupByMap>(
    input.initialFields?.[3] ?? DEFAULT_RIGHT
  );

  useEffect(() => {
    if (!input.onChange) {
      return;
    }
    input.onChange([bottomValue, leftValue, topValue, rightValue]);
  }, [bottomValue, topValue, leftValue, rightValue]);

  useEffect(() => {
    // Only sync to global groupBy when the user hasn't saved a custom bottom field.
    if (input.initialFields?.[0]) return;
    setBottomValue(groupBy);
  }, [groupBy]);

  return (
    <Container>
      <BottomFieldSelector>
        <XdaDataPickerSelector groupByValue={bottomValue} setGroupBy={setBottomValue} />
      </BottomFieldSelector>

      <LeftFieldSelector>
        <XdaDataPickerSelector groupByValue={leftValue} setGroupBy={setLeftValue} />
      </LeftFieldSelector>

      <TopFieldSelector>
        <XdaDataPickerSelector groupByValue={topValue} setGroupBy={setTopValue} />
      </TopFieldSelector>

      <RightFieldSelector>
        <XdaDataPickerSelector groupByValue={rightValue} setGroupBy={setRightValue} />
      </RightFieldSelector>

      <DotWrapper>
        <Dot />
      </DotWrapper>
    </Container>
  );
}

const Container = styled.div`
  width: 100%;
  height: 120px;
  display: grid;
  align-items: center;
  grid-template-columns: minmax(1fr, 80px) minmax(1fr, 80px) minmax(1fr, 80px);
  grid-template-rows: minmax(1fr, 80px) minmax(1fr, 80px) minmax(1fr, 80px);
`;

const FieldSelectorWrapper = styled.div`
  align-self: center;
  justify-self: center;
  span.label {
    width: 80px;
    display: inline-block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;

const DotWrapper = styled.div`
  grid-row-start: 2;
  grid-column-start: 2;
  align-self: center;
  justify-self: center;
`;

const Dot = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: black;
`;

const TopFieldSelector = styled(FieldSelectorWrapper)`
  grid-row-start: 1;
  grid-column-start: 2;
`;
const LeftFieldSelector = styled(FieldSelectorWrapper)`
  grid-row-start: 2;
  grid-column-start: 1;
`;
const BottomFieldSelector = styled(FieldSelectorWrapper)`
  grid-row-start: 3;
  grid-column-start: 2;
`;
const RightFieldSelector = styled(FieldSelectorWrapper)`
  grid-row-start: 2;
  grid-column-start: 3;
`;
